import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getAeAirlineDetails } from "../../../../Api/apiUtils";
import airplaneIcon from "../../../../assets/img/airplane.svg";
import homeIcon from "../../../../assets/img/home.svg";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Badge,
  Field,
  TextInput,
  Toolbar,
  Table,
  Loader,
  EmptyState,
} from "../../../../ui";
import "./aeReports.scss";

type CategoryStat = { bookedSlots: number; totalSlots: number };

type SlotDetail = {
  categoryName?: string;
  bookedSlots?: number;
  totalSlots?: number;
  /** International slot counts when API sends these instead of booked/total. */
  ibslots?: number;
  itslots?: number;
  /** Domestic slot counts when API sends these instead of booked/total. */
  dbslots?: number;
  dtslots?: number;
};

type AcademicSlotDetail = {
  slotDate?: string;
  slotsDetails?: SlotDetail[];
};

type ApiBlock = {
  slotType?: string;
  academicSlotDetails?: AcademicSlotDetail[];
};

/** Legacy merged row: intl fields + `d-*` prefixed domestic. */
type MergedRow = {
  date?: string;
  isInternational?: boolean;
  ibslots?: number;
  itslots?: number;
  dbslots?: number;
  dtslots?: number;
  "d-dbslots"?: number;
  "d-dtslots"?: number;
} & Record<string, CategoryStat | string | number | boolean | undefined>;

const getTodayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDaysYmd = (ymd: string, days: number): string => {
  const [y, mo, da] = ymd.split("-").map((n) => parseInt(n, 10));
  const d = new Date(y, mo - 1, da);
  d.setDate(d.getDate() + days);
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const toNum = (v: unknown): number => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

const readAirlineDetailsPayload = (raw: unknown): ApiBlock[] => {
  if (Array.isArray(raw)) return raw as ApiBlock[];
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data as ApiBlock[];
    if (o.data && typeof o.data === "object") {
      const d = o.data as Record<string, unknown>;
      if (Array.isArray(d.content)) return d.content as ApiBlock[];
    }
    if (Array.isArray(o.content)) return o.content as ApiBlock[];
  }
  return [];
};

/** Legacy Ant thresholds: percentage 0–100 (coerce string). <80 low, 80–99 mid, ≥100 high. */
const getLevelByPercentage = (percentage: number): "low" | "mid" | "high" => {
  const p = Number(percentage);
  if (p < 80) return "low";
  if (p >= 80 && p < 100) return "mid";
  return "high";
};

/** Legacy: `(Sunday)` + `01 March 2026` (DD MMMM YYYY + weekday). */
const formatDateParts = (ymd: string): { weekday: string; formatted: string } => {
  if (!ymd) return { weekday: "", formatted: "" };
  const d = new Date(ymd.includes("T") ? ymd : `${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return { weekday: "", formatted: ymd };
  const weekday = d.toLocaleDateString("en-GB", { weekday: "long" });
  const formatted = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return { weekday, formatted };
};

type Processed = {
  rows: MergedRow[];
  /** Legacy: `Array.from(categoriesSet)` — same names & order for Intl + Domestic columns. */
  categories: string[];
  iACBooked: number;
  iACTotal: number;
  ACBooked: number;
  ACTotal: number;
  iCCBooked: Record<string, number>;
  iCCTotal: Record<string, number>;
  CCBooked: Record<string, number>;
  CCTotal: Record<string, number>;
};

/** Same as legacy `processApiResponse` (strict International, index merge, shared category set). */
function processApiResponse(data: unknown): Processed {
  const response = readAirlineDetailsPayload(data);
  const categoriesSet = new Set<string>();
  const internationalSlots: Record<string, unknown>[] = [];
  const domesticSlots: Record<string, unknown>[] = [];
  let intAllCountBooked = 0;
  let intAllCountTotal = 0;
  let allCountBooked = 0;
  let allCountTotal = 0;
  const intCategoryCountbooked: Record<string, number> = {};
  const intCategoryCounttotal: Record<string, number> = {};
  const categoryCountbooked: Record<string, number> = {};
  const categoryCounttotal: Record<string, number> = {};

  response.forEach((slotType) => {
    const isInternational = slotType.slotType === "International";
    for (const slotDetail of slotType.academicSlotDetails || []) {
      const date = slotDetail.slotDate;
      const row: Record<string, unknown> = { date, isInternational };
      if (isInternational) {
        row.ibslots = 0;
        row.itslots = 0;
      } else {
        row.dbslots = 0;
        row.dtslots = 0;
      }
      for (const slot of slotDetail.slotsDetails || []) {
        const categoryName = slot.categoryName ?? "Unknown";
        let bookedSlots: number;
        let totalSlots: number;
        if (isInternational) {
          bookedSlots = toNum(slot.ibslots ?? slot.bookedSlots);
          totalSlots = toNum(slot.itslots ?? slot.totalSlots);
          intAllCountBooked += bookedSlots;
          intAllCountTotal += totalSlots;
          intCategoryCountbooked[categoryName] =
            (intCategoryCountbooked[categoryName] || 0) + bookedSlots;
          intCategoryCounttotal[categoryName] =
            (intCategoryCounttotal[categoryName] || 0) + totalSlots;
          row.ibslots = (row.ibslots as number) + bookedSlots;
          row.itslots = (row.itslots as number) + totalSlots;
        } else {
          bookedSlots = toNum(slot.dbslots ?? slot.bookedSlots);
          totalSlots = toNum(slot.dtslots ?? slot.totalSlots);
          allCountBooked += bookedSlots;
          allCountTotal += totalSlots;
          categoryCountbooked[categoryName] =
            (categoryCountbooked[categoryName] || 0) + bookedSlots;
          categoryCounttotal[categoryName] =
            (categoryCounttotal[categoryName] || 0) + totalSlots;
          row.dbslots = (row.dbslots as number) + bookedSlots;
          row.dtslots = (row.dtslots as number) + totalSlots;
        }
        categoriesSet.add(categoryName);
        row[categoryName] = { bookedSlots, totalSlots };
      }
      if (isInternational) internationalSlots.push(row);
      else domesticSlots.push(row);
    }
  });

  const mergedSlots: MergedRow[] = [];
  const maxLength = Math.max(internationalSlots.length, domesticSlots.length);
  for (let i = 0; i < maxLength; i++) {
    const mergedSlot = { ...(internationalSlots[i] || {}) } as MergedRow;
    const dom = domesticSlots[i];
    if (dom) {
      const m = mergedSlot as Record<string, unknown>;
      Object.keys(dom).forEach((key) => {
        m[`d-${key}`] = dom[key];
      });
    }
    mergedSlots.push(mergedSlot);
  }

  return {
    rows: mergedSlots,
    categories: Array.from(categoriesSet),
    iACBooked: intAllCountBooked,
    iACTotal: intAllCountTotal,
    ACBooked: allCountBooked,
    ACTotal: allCountTotal,
    iCCBooked: intCategoryCountbooked,
    iCCTotal: intCategoryCounttotal,
    CCBooked: categoryCountbooked,
    CCTotal: categoryCounttotal,
  };
}

const CategoryCell: React.FC<{ stat?: CategoryStat }> = ({ stat }) => {
  if (!stat) {
    return <div className="ae-heat ae-heat--empty">-</div>;
  }
  const { bookedSlots, totalSlots } = stat;
  const percentageNum = totalSlots !== 0 ? (bookedSlots / totalSlots) * 100 : 0;
  const level = getLevelByPercentage(percentageNum);
  return (
    <div className={`ae-heat ae-heat--${level}`}>
      <span className="ds-strong ds-num">
        {bookedSlots}/{totalSlots}
      </span>
      <br />({Math.round(percentageNum)}%)
    </div>
  );
};

/**
 * Demo Status Details — airline slot utilization (intl vs domestic by category).
 * Category columns come only from the airline/details API response (`categoryName` discovery order).
 */
const DemoStatusDetails: React.FC = () => {
  const initialFrom = useMemo(() => getTodayYmd(), []);
  const initialTo = useMemo(() => addDaysYmd(getTodayYmd(), 7), []);

  const [fromDate, setFromDate] = useState(initialFrom);
  const [toDate, setToDate] = useState(initialTo);
  const [loading, setLoading] = useState(false);
  const [processed, setProcessed] = useState<Processed | null>(null);

  const runFetch = useCallback(async (from: string, to: string) => {
    if (!from || !to) {
      toast.error("Please select a date range.");
      return;
    }
    if (from > to) {
      toast.error("From date must be on or before to date.");
      return;
    }
    setLoading(true);
    try {
      const raw = await getAeAirlineDetails(from, to);
      setProcessed(processApiResponse(raw));
    } catch (e) {
      console.error(e);
      toast.error("Could not load demo status details.");
      setProcessed(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void runFetch(initialFrom, initialTo);
    }, 200);
    return () => window.clearTimeout(t);
  }, [initialFrom, initialTo, runFetch]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void runFetch(fromDate, toDate);
  };

  const p = processed;
  const catCount = p?.categories.length ?? 0;
  const tableMinWidth = catCount > 0 ? 220 + 150 * catCount * 2 : 400;

  return (
    <Page>
      <PageHeader
        eyebrow="AE Reports"
        title="Demo Status Details"
        subtitle="Airline slot utilization by date — international vs domestic, split by course category."
      />

      <Card>
        <form onSubmit={onSubmit}>
          <Toolbar>
            <Field label="From date" required>
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="date"
                  required
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="To date" required>
              {(ctl) => (
                <TextInput
                  {...ctl}
                  type="date"
                  required
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              )}
            </Field>
            <div className="ae-reports__actions">
              <Button variant="primary" type="submit" loading={loading}>
                Submit
              </Button>
            </div>
          </Toolbar>
        </form>
      </Card>

      {p && !loading && (
        <div className="ae-reports__summary">
          <Badge tone="neutral">
            International (all): {p.iACBooked}/{p.iACTotal}
          </Badge>
          <Badge tone="neutral">
            Domestic (all): {p.ACBooked}/{p.ACTotal}
          </Badge>
        </div>
      )}

      {loading ? (
        <Loader label="Loading demo status…" />
      ) : p && p.rows.length > 0 ? (
        <Card flush className="ae-reports__scroll ae-reports__scroll--65">
          {/* minWidth is data-driven: one 150px column per category, twice (intl + domestic). */}
          <Table
            caption="Demo slot utilization by date"
            className="ae-reports__grid ae-demo"
            style={{ minWidth: tableMinWidth }}
          >
            <thead>
              <tr>
                <th rowSpan={2} className="ae-reports__sticky-col ae-reports__sticky-col--date">
                  Date
                </th>
                <th colSpan={Math.max(1, catCount)}>
                  International · {p.iACBooked}/{p.iACTotal}
                </th>
                <th colSpan={Math.max(1, catCount)}>
                  Domestic · {p.ACBooked}/{p.ACTotal}
                </th>
              </tr>
              <tr>
                {catCount === 0 ? (
                  <>
                    <th>—</th>
                    <th>—</th>
                  </>
                ) : (
                  <>
                    {p.categories.map((category) => (
                      <th key={`i-${category}`}>
                        <span className="ds-strong">{category}</span>{" "}
                        <span className="ds-num">
                          {p.iCCBooked[category] || 0}/{p.iCCTotal[category] || 0}
                        </span>
                      </th>
                    ))}
                    {p.categories.map((category) => (
                      <th key={`d-${category}`}>
                        <span className="ds-strong">{category}</span>{" "}
                        <span className="ds-num">
                          {p.CCBooked[category] || 0}/{p.CCTotal[category] || 0}
                        </span>
                      </th>
                    ))}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {p.rows.map((row, idx) => (
                <tr key={`${row.date ?? "r"}-${idx}`}>
                  <td className="ae-reports__sticky-col ae-reports__sticky-col--date">
                    <Link
                      to={{
                        pathname: `${process.env.PUBLIC_URL}/pages/ae-reports/date-view`,
                        search: `?date=${encodeURIComponent(String(row.date ?? ""))}`,
                      }}
                      state={{ dateViewDetails: [] as unknown[] }}
                      className="ae-demo__date"
                    >
                      {(() => {
                        const { weekday, formatted } = formatDateParts(String(row.date ?? ""));
                        const intlLine =
                          row.isInternational === true ? `${row.ibslots ?? 0}/${row.itslots ?? 0}` : "";
                        const dbs = (row["d-dbslots"] as number | undefined) ?? 0;
                        const dts = (row["d-dtslots"] as number | undefined) ?? 0;
                        return (
                          <>
                            <span className="ae-demo__weekday">({weekday})</span>
                            <span className="ds-strong">{formatted}</span>
                            <span className="ae-demo__total">
                              <img src={airplaneIcon} alt="" />
                              Total Slots: <span className="ae-demo__total-value ds-num">{intlLine}</span>
                            </span>
                            <span className="ae-demo__total">
                              <img src={homeIcon} alt="" />
                              Total Slots:{" "}
                              <span className="ae-demo__total-value ds-num">
                                {dbs}/{dts}
                              </span>
                            </span>
                          </>
                        );
                      })()}
                    </Link>
                  </td>
                  {catCount === 0 ? (
                    <>
                      <td>—</td>
                      <td>—</td>
                    </>
                  ) : (
                    <>
                      {p.categories.map((cat) => (
                        <td key={`${idx}-i-${cat}`} className="ae-demo__cell">
                          <CategoryCell stat={row[cat] as CategoryStat | undefined} />
                        </td>
                      ))}
                      {p.categories.map((cat) => (
                        <td key={`${idx}-d-${cat}`} className="ae-demo__cell">
                          <CategoryCell stat={row[`d-${cat}`] as CategoryStat | undefined} />
                        </td>
                      ))}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : !loading ? (
        <EmptyState title="No data for this range." description="Try a wider date range, then select Submit." />
      ) : null}
    </Page>
  );
};

export default DemoStatusDetails;
